import { type FC, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import {
  ProgressBar,
  ValuePosition,
} from '@/components/progressBars/LinearProgressBar';
import { ProgressBarMode } from '@/components/progressBars/LinearProgressBar/styled';
import { Modals } from '@/helpers/modals';
import { useNavigation } from '@/hooks/navigation';
import { cx } from '@/modules/common/helpers/classNames';
import { useMissionLevels } from '@/modules/promotions/templates/HeadlinePromoTemplate/components/MissionLevels/hooks/useMissionLevels';
import type { IHeadlinePromoPageConfig } from '@/modules/promotions/templates/HeadlinePromoTemplate/helpers';
import { useGetCommonStore, useModalsStore } from '@/store';
import { ScHeadlinePromoSection } from './styled';

export interface IHeadlinePromoSectionProps {
  config: IHeadlinePromoPageConfig;
  onLinkClick?: () => void;
}

export const HeadlinePromoSection: FC<IHeadlinePromoSectionProps> = ({
  config,
  onLinkClick,
}) => {
  const { t } = useTranslation(config.namespace);
  const { open } = useModalsStore();

  const { navigate } = useNavigation();

  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const { levels, currentLevelIndex } = useMissionLevels(
    config.levelsConfig,
    config.groupPrefix,
    config.useFullNameAsGroupId
  );
  const currentLevel = levels[currentLevelIndex];
  const allLevelsCompleted = levels.at(-1)?.percent === 100;

  const handleButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onLinkClick?.();
      if (!isAuthenticated) {
        open(Modals.Login);
        return;
      }
      navigate(config.route);
    },
    [onLinkClick, navigate, config.route, isAuthenticated, open]
  );

  if (isAuthenticated && !currentLevel) return null;
  if (!(isAuthenticated || config.levelsConfig.length)) return null;

  const issued = allLevelsCompleted
    ? levels.some((level) => level.state === 'issued')
    : currentLevel?.state === 'issued';

  const ctaLabel = (() => {
    if (!isAuthenticated) return t('widget.login');
    return t('widget.cta');
  })();

  const rewardContent = allLevelsCompleted ? (
    t('widget.allLevelsCompleted')
  ) : (
    <Trans
      components={{
        span: <span className="bold" />,
      }}
      i18nKey="widget.reward"
      ns={config.namespace}
      values={{
        reward: currentLevel.rewardLabel,
      }}
    />
  );

  return (
    <ScHeadlinePromoSection>
      <div className="info">
        <div className="level-info">
          <div className="text">
            {isAuthenticated ? (
              <>
                <span>
                  <b>{t('widget.title')}</b>
                </span>
                <span>{rewardContent}</span>
              </>
            ) : (
              <span>
                <b>{t('widget.guestTitle')}</b>
              </span>
            )}
          </div>
          <div className="addons">
            <div className="circle">
              <ImageComponent
                alt=""
                className="icon"
                height={48}
                src={
                  isAuthenticated
                    ? currentLevel.image
                    : config.levelsConfig[0].image
                }
                width={48}
              />
            </div>
          </div>
        </div>
        {isAuthenticated && (
          <div
            className={cx('progress', (issued || allLevelsCompleted) && 'full')}
          >
            <ProgressBar
              borderRadius="24px"
              borderRadiusInner={issued ? '24px' : '24px 0 0 24px'}
              completedText={issued ? t('widget.claim') : undefined}
              height={24}
              mode={issued ? ProgressBarMode.GREEN : ProgressBarMode.SECONDARY}
              showTo={false}
              showValue={ValuePosition.CENTER}
              value={currentLevel.percent}
              withUnits
            />
          </div>
        )}
      </div>
      <Button className="loginBtn" navigation onClick={handleButtonClick}>
        {ctaLabel}
      </Button>
    </ScHeadlinePromoSection>
  );
};
